import { getIdToken } from "firebase/auth";
import { auth } from "./firebase";
import { useStore } from "@/store";

const READ_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/**
 * Thrown instead of issuing a mutating request as a viewer.
 *
 * The backend already rejects these — every mutating core route is gated on
 * `min_role=editor` or higher, and `firestore.rules` requires `isOrgEditor` for
 * the direct-write collections. This is the client-side backstop so a write
 * affordance that escaped `useOrgRole` fails with a readable message instead of
 * an opaque 403.
 *
 * Only `viewer` is blocked here, and that is the whole point: `editor` is a
 * writer, so it must pass. Widening this to "anything below admin" would break
 * every editor's app.
 */
export class ViewerReadOnlyError extends Error {
  constructor() {
    super("Viewers can't make changes. Ask an organization admin.");
    this.name = "ViewerReadOnlyError";
  }
}

interface AuthenticatedFetchOptions {
  /**
   * Let a viewer issue this mutating request.
   *
   * The backstop above assumes every mutating route is gated at `editor` or
   * higher. That is true of everything that writes *org data*, and false for a
   * request that writes only the caller's own account state — connecting an
   * external MCP client to a domain they can already read, and revoking one.
   * Revocation especially must never be blocked: a viewer cutting off a client
   * they no longer trust is the one write nobody should have to ask an admin for.
   *
   * Set this only where core itself permits a viewer, and never as a way to
   * quiet an unexpected `ViewerReadOnlyError` — that error means the affordance
   * should have been hidden.
   */
  isViewerAllowed?: boolean;
}

export async function authenticatedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
  options: AuthenticatedFetchOptions = {},
): Promise<Response> {
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error("User not logged in");
  }

  // Only a *resolved* viewer is blocked. A null role means the org hasn't
  // loaded yet or doesn't exist — signup creates the org before any membership
  // exists, so blocking on `!== "admin"` would break onboarding.
  const method = (init.method ?? "GET").toUpperCase();
  if (
    !READ_METHODS.has(method) &&
    !options.isViewerAllowed &&
    useStore.getState().orgRole === "viewer"
  ) {
    throw new ViewerReadOnlyError();
  }

  const token = await getIdToken(currentUser);
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);

  return fetch(input, {
    ...init,
    headers,
  });
}
